import { Loader2, MapPin, Users } from 'lucide-react'
import { useLocale } from '../../i18n/locale'
import { useT } from '../../i18n/t'
import {
  usePublicSocialProofStats,
  type PublicSocialProofStatsState,
} from '../../hooks/usePublicSocialProofStats'

function formatStat(n: number, localeTag: string): string {
  return n.toLocaleString(localeTag)
}

function SocialProofHeadline({
  users,
  className = '',
}: {
  users: number
  className?: string
}) {
  const { t } = useT()
  const { localeTag } = useLocale()
  const count = formatStat(users, localeTag)

  return (
    <span className={className}>
      {t('landing.socialProof.headline.before')}{' '}
      <span className="font-extrabold tabular-nums text-brand">{count}</span>{' '}
      {t('landing.socialProof.headline.after')}
    </span>
  )
}

export type CommunityJoinStatsProps = {
  variant?: 'hero' | 'headline' | 'pills' | 'text'
  className?: string
  stats: PublicSocialProofStatsState
}

export function CommunityJoinStatsView({
  variant = 'text',
  className = '',
  stats,
}: CommunityJoinStatsProps) {
  const { t } = useT()
  const { localeTag } = useLocale()

  const loading = stats.status === 'loading' || stats.status === 'idle'
  const showNumbers = stats.status === 'success'

  const fallbackText = t('landing.socialProof.headline.fallback')

  if (variant === 'hero') {
    return (
      <div
        className={`mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center ${className}`}
        aria-live="polite"
      >
        {loading ? (
          <p className="inline-flex items-center gap-2 text-[14px] font-medium text-white/70 md:text-[15px]">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('landing.socialProof.headline.loading')}
          </p>
        ) : (
          <>
            <p className="max-w-[48ch] text-[14px] font-semibold leading-snug text-white/90 md:text-[15px]">
              {showNumbers ? (
                <SocialProofHeadline users={stats.users} />
              ) : (
                fallbackText
              )}
            </p>
            {showNumbers ? (
              <div
                className="flex flex-wrap gap-2"
                aria-label={t('landing.socialProof.statsAria')}
              >
                <span className="inline-flex items-center gap-1.5 pds-tag pds-tag--on-dark px-3 py-1.5 text-[13px] font-semibold text-white backdrop-blur-sm">
                  <Users className="h-3.5 w-3.5 text-brand" aria-hidden />
                  {t('landing.stats.users', { count: formatStat(stats.users, localeTag) })}
                </span>
                <span className="inline-flex items-center gap-1.5 pds-tag pds-tag--on-dark px-3 py-1.5 text-[13px] font-semibold text-white backdrop-blur-sm">
                  <MapPin className="h-3.5 w-3.5 text-brand" aria-hidden />
                  {t('landing.stats.cities', { count: formatStat(stats.cities, localeTag) })}
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>
    )
  }

  if (variant === 'headline') {
    if (loading) {
      return (
        <span className={`inline-flex items-center gap-2 text-ink/70 ${className}`}>
          <Loader2 className="h-6 w-6 shrink-0 animate-spin" aria-hidden />
          {t('landing.socialProof.headline.loading')}
        </span>
      )
    }
    return (
      <span className={className}>
        {showNumbers ? (
          <SocialProofHeadline users={stats.users} />
        ) : (
          fallbackText
        )}
      </span>
    )
  }

  if (variant === 'pills') {
    if (!showNumbers) return null
    return (
      <div
        className={`flex flex-wrap gap-3 ${className}`}
        aria-label={t('landing.socialProof.statsAria')}
      >
        <span className="inline-flex items-center gap-2 pds-tag px-4 py-2 text-sm font-semibold text-ink">
          <Users className="h-4 w-4 text-brand" aria-hidden />
          {t('landing.stats.users', { count: formatStat(stats.users, localeTag) })}
        </span>
        <span className="inline-flex items-center gap-2 pds-tag px-4 py-2 text-sm font-semibold text-ink">
          <MapPin className="h-4 w-4 text-brand" aria-hidden />
          {t('landing.stats.cities', { count: formatStat(stats.cities, localeTag) })}
        </span>
      </div>
    )
  }

  if (loading) {
    return (
      <p
        className={`mt-4 flex items-center gap-2 text-[14px] font-medium text-ink/55 md:text-[15px] ${className}`}
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {t('landing.socialProof.headline.loading')}
      </p>
    )
  }

  return (
    <p
      className={`mt-4 max-w-[52ch] text-[15px] font-medium leading-relaxed text-ink/75 md:text-[16px] ${className}`}
    >
      {showNumbers ? (
        <SocialProofHeadline users={stats.users} />
      ) : (
        fallbackText
      )}
    </p>
  )
}

type CommunityJoinStatsConnectedProps = Omit<CommunityJoinStatsProps, 'stats'> & {
  stats?: PublicSocialProofStatsState
}

export function CommunityJoinStats({
  stats: statsOverride,
  ...props
}: CommunityJoinStatsConnectedProps) {
  const fetched = usePublicSocialProofStats()
  return <CommunityJoinStatsView stats={statsOverride ?? fetched} {...props} />
}
